import { Add as AddIcon } from "@mui/icons-material";
import { IconButton } from "@mui/material";
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type FC,
  type PropsWithChildren,
  type ReactNode,
} from "react";
import useEvent from "react-use-event-hook";

import { usePagingStickyEmulation, ViewHandlingPanel } from "@ryde/controls";
import {
  useCrudNavigate,
  useFiltersActiveState,
  useLayoutMainContext,
  usePageContext,
} from "@ryde/redux";
import {
  DrawerType,
  isRideEntity,
  useDrawers,
  type RydeAppUnit,
} from "@ryde/shared";

import { PanelTopSearchMobileLine } from "./bottom-widget";
import { PageHeaderMobileSearchButton } from "./elements";
import { PageHeadersFiltersMobileSidebar } from "./filters";
import {
  FilterFieldsMobileWrapper,
  PageHeaderMobileContainerStyled,
  PageHeaderMobileStyled,
  ViewsPanelWrapperMobile,
} from "./page-header.styled";
import {
  PanelTopStatBookings,
  PanelTopStatRides,
  PanelTopStatRidesLiveMap,
} from "./panels-stat";

interface IHeaderMobileProps extends PropsWithChildren {
  mainTitle: string;
  topWidgetComponent?: ReactNode;
  bottomWidgetComponent?: ReactNode;
  mobileStatLeftComponent?: ReactNode;
}

export const PageHeaderMobile: FC<IHeaderMobileProps> = ({
  mainTitle,
  topWidgetComponent,
  bottomWidgetComponent,
  mobileStatLeftComponent,
  children,
}) => {
  const [isFilterSidebarOpen, setIsFilterSidebarOpen] = useState(false);
  const { entity } = usePageContext();
  const { navigateToPreCreateOrNew } = useCrudNavigate(entity as RydeAppUnit);
  const mobileHeaderRef = useRef<HTMLDivElement>(null);
  const mobileHeaderSecondLineRef = useRef<HTMLDivElement>(null);
  const mobilePageHeaderRef = useRef<HTMLDivElement>(null);
  const { setHeaderElement, headerElement } = useLayoutMainContext();
  usePagingStickyEmulation({ pagingRef: mobileHeaderRef });
  usePagingStickyEmulation({ pagingRef: mobileHeaderSecondLineRef });
  const { openDrawer } = useDrawers();

  useEffect(() => {
    if (headerElement) {
      return;
    }

    setHeaderElement(mobilePageHeaderRef.current);
  }, [headerElement, setHeaderElement]);

  useEffect(() => {
    return () => {
      setHeaderElement(null);
    };
  }, [setHeaderElement]);

  const onFiltersOpenClick = (): void => {
    setIsFilterSidebarOpen(true);
  };

  const onFiltersApply = useEvent(() => {
    setIsFilterSidebarOpen(false);
  });

  const isLiveMapRoute = entity === "rideMap";

  const isRidesRoute = useMemo(() => {
    return isRideEntity(entity);
  }, [entity]);

  const isBookingsRoute = useMemo(() => {
    return entity === "booking";
  }, [entity]);

  const isCommonSearchRoute = useMemo(() => {
    return (
      Boolean(entity) &&
      entity !== "unknown" &&
      !isBookingsRoute &&
      !isRidesRoute
    );
  }, [entity, isBookingsRoute, isRidesRoute]);

  // Passengers render the same mobile line from their page bottomWidget.
  const showCommonSearchMobileLine =
    isCommonSearchRoute && entity !== "passenger";

  const { isAnyFilterActive } = useFiltersActiveState();

  const onAddClick = useEvent(() => {
    if (entity === "booking") {
      openDrawer({
        drawerProps: {
          type: DrawerType.AddNewBulkBookings,
        },
      });
    } else {
      navigateToPreCreateOrNew(true);
    }
  });

  return (
    <>
      <PageHeaderMobileContainerStyled ref={mobilePageHeaderRef}>
        <PageHeaderMobileStyled ref={mobileHeaderRef}>
          {Boolean(topWidgetComponent) && <span>{topWidgetComponent}</span>}
          <div className="buttons-container">
            <IconButton onClick={onAddClick} className="add-button">
              <AddIcon color="primary" />
            </IconButton>
            <PageHeaderMobileSearchButton
              isAnyFilterActive={isAnyFilterActive}
              onClick={onFiltersOpenClick}
            />
          </div>
        </PageHeaderMobileStyled>
        <div
          ref={mobileHeaderSecondLineRef}
          data-e2e="second-line-header-mobile-sticky"
          className="mobile-header-second-line"
        >
          {isRidesRoute && !isLiveMapRoute && <PanelTopStatRides />}
          {isLiveMapRoute && (
            <PanelTopStatRidesLiveMap leftComponent={mobileStatLeftComponent} />
          )}
          {isBookingsRoute && <PanelTopStatBookings />}
          {showCommonSearchMobileLine && <PanelTopSearchMobileLine />}
        </div>

        {Boolean(bottomWidgetComponent) && bottomWidgetComponent}
      </PageHeaderMobileContainerStyled>

      {isFilterSidebarOpen && (
        <PageHeadersFiltersMobileSidebar
          mainTitle={mainTitle}
          onApply={onFiltersApply}
          setIsFilterSidebarOpen={setIsFilterSidebarOpen}
        >
          <FilterFieldsMobileWrapper>
            {children}
            <ViewsPanelWrapperMobile>
              <ViewHandlingPanel />
            </ViewsPanelWrapperMobile>
          </FilterFieldsMobileWrapper>
        </PageHeadersFiltersMobileSidebar>
      )}
    </>
  );
};
